import { LocalStorageManager } from './localStorage.js'
import staticData from '../data/staticData.json'

// Hybrid API: Static data for deployment, localStorage for admin editing
// Public pages read from staticData.json (deployed with site)
// Admin panel uses localStorage (browser-specific, for editing)

// Auth API - Simple admin authentication
export const authAPI = {
  login: async (credentials) => {
    // Simple hardcoded admin credentials
    if (credentials.username === 'admin' && credentials.password === 'helloheman2024') {
      const authData = {
        token: 'admin-token-' + Date.now(),
        user: { username: 'admin', role: 'admin' },
        timestamp: new Date().toISOString()
      }
      
      LocalStorageManager.setAdminAuth(authData)
      
      return {
        success: true,
        token: authData.token,
        user: authData.user
      }
    }
    
    throw new Error('Invalid credentials')
  },
  
  logout: () => {
    LocalStorageManager.clearAdminAuth()
    return { success: true }
  },
  
  isAuthenticated: () => {
    return LocalStorageManager.isAdminAuthenticated()
  }
}

// Projects API - Uses static data (deployed) with localStorage fallback (admin)
export const projectsAPI = {
  getAll: async () => {
    // Use static data from JSON file (this deploys with your site)
    const projects = staticData.projects && staticData.projects.length > 0 
      ? staticData.projects 
      : LocalStorageManager.getProjects()
    return { data: { projectsList: projects } }
  },
  
  getById: async (id) => {
    // Use static data from JSON file
    const projects = staticData.projects && staticData.projects.length > 0 
      ? staticData.projects 
      : LocalStorageManager.getProjects()
    const project = projects.find(p => p.id === id)
    return { data: project }
  },
  
  create: async (projectData) => {
    const project = LocalStorageManager.addProject(projectData)
    return { data: project }
  },
  
  update: async (id, projectData) => {
    const project = LocalStorageManager.updateProject(id, projectData)
    return { data: project }
  },
  
  delete: async (id) => {
    LocalStorageManager.deleteProject(id)
    return { success: true }
  }
}

// About API - Uses static data (deployed) with localStorage fallback
export const aboutAPI = {
  get: async () => {
    // Use static data from JSON file
    const aboutData = staticData.about || LocalStorageManager.getAboutData()
    return { data: [aboutData] } // Return as array to match expected format
  },
  
  update: async (aboutData) => {
    LocalStorageManager.saveAboutData(aboutData)
    return { data: aboutData }
  }
}

// Now API - Uses static data (deployed) with localStorage fallback
export const nowAPI = {
  get: async () => {
    // Use static data from JSON file
    const nowData = staticData.now || LocalStorageManager.getNowData()
    return { data: [nowData] } // Return as array to match expected format
  },
  
  update: async (nowData) => {
    LocalStorageManager.saveNowData(nowData)
    return { data: nowData }
  }
}

// Home Video API - Uses static data (deployed) with localStorage fallback
export const homeVideoAPI = {
  get: async () => {
    // Use static data from JSON file
    const videoData = staticData.homeVideo || LocalStorageManager.getHomeVideoData()
    return { data: [videoData] } // Return as array to match expected format
  },
  
  create: async (videoData) => {
    LocalStorageManager.saveHomeVideoData(videoData)
    return { data: videoData }
  },
  
  update: async (videoData) => {
    LocalStorageManager.saveHomeVideoData(videoData)
    return { data: videoData }
  }
}

// Initialize default data on first load
export const initializeData = () => {
  // Only initialize localStorage defaults if static data is empty
  // This helps with local development before exporting
  if (!staticData.projects || staticData.projects.length === 0) {
    LocalStorageManager.initializeDefaultData()
  }
  console.log('📊 Using static data:', {
    projects: staticData.projects?.length || 0,
    hasAbout: !!staticData.about,
    hasNow: !!staticData.now,
    hasHomeVideo: !!staticData.homeVideo
  })
}

export default {
  authAPI,
  projectsAPI,
  aboutAPI,
  nowAPI,
  homeVideoAPI,
  initializeData
}
